import { ReactComponent as DeleteIcon } from '@assets/images/deleteIcon.svg';
import { ReactComponent as MinusGrayIcon } from '@assets/images/minusGrayIcon.svg';
import { ReactComponent as PlusGrayIcon } from '@assets/images/plusGrayIcon.svg';
import { Tooltip } from '@components/common/Tooltip';
import { cn } from '@utils';
import './style.scss';
import { ChangeEvent } from 'react';
import { PHYSICAL_QTY_MAX_FOR_B2B } from '@config/constants';

type QuantitySelectorProps = {
  onChange: (qty: number) => void;
  onRemove: () => void;
  qty: number;
  minQty: number;
  loading?: boolean;
  disabledIncrement?: boolean;
  infoMessage: string;
  isBulkOrder?: boolean;
};

type TooltipContentProps = {
  text: string;
};

const TooltipContent = ({ text }: TooltipContentProps) => (
  <div className="quantity-selector__tooltip" data-testid="quantity-selector-tooltip">
    {text}
  </div>
);

const QuantitySelector = ({
  qty, onChange, onRemove, loading, disabledIncrement, infoMessage, minQty, isBulkOrder = false,
}: QuantitySelectorProps) => {
  const handleBulkChange = (e: ChangeEvent<HTMLInputElement>) => {
    const sanitizedValue = e.target.value.replace(/[^\d]/g, '');
    const value = sanitizedValue ? parseInt(sanitizedValue, 10) : minQty;

    const clampedValue = Math.min(
      Math.max(value, minQty),
      PHYSICAL_QTY_MAX_FOR_B2B,
    );

    if (clampedValue !== qty) {
      onChange(clampedValue);
    }
  };

  return (
    <div className={cn('quantity-selector', { 'bulk-order': isBulkOrder })} data-testid="quantity-selector">
      <button
        type="button"
        className="quantity-selector__btn"
        data-testid="quantity-selector-decrement-btn"
        onClick={() => !loading && (qty > minQty ? onChange(qty - 1) : onRemove())}
      >
        {qty > minQty ? <MinusGrayIcon /> : <DeleteIcon className="custom-delete-icon" />}
      </button>
      {isBulkOrder ? (
        <input
          type="number"
          name="counter-value-editor"
          value={qty || ''}
          onChange={handleBulkChange}
          className="counter-value-input"
        />
      ) : (
        <div className="quantity-selector__qty">{qty}</div>
      )}
      <Tooltip disabled={!disabledIncrement} content={<TooltipContent text={infoMessage} />}>
        <button
          type="button"
          data-testid="quantity-selector-increment-btn"
          className={cn('quantity-selector__btn', { disabled: disabledIncrement })}
          onClick={() => !loading && !disabledIncrement && onChange(qty + 1)}
        >
          <PlusGrayIcon />
        </button>
      </Tooltip>
    </div>
  );
};

export { QuantitySelector };
